import { create } from "zustand";

import type { LockSignal } from "../api/types";

/**
 * Карточки замка исхода: те, что ждут решения, и недавно закрытые.
 *
 * # Почему замещение по id, а не добавление
 *
 * Карточка приезжает одним типом сообщения на ВСЕ свои состояния: появилась,
 * состояние выросло с safe до settled, взята, пропущена, отозвана, сгорела.
 * Отдельные типы «карточка» и «решение» заставили бы склеивать их по
 * идентификатору руками, то есть держать порядок доставки, которого у Pub/Sub
 * нет. То же правило, что у useSignals и order_update.
 *
 * Идентификатор при этом ДЕТЕРМИНИРОВАННЫЙ (окно + рынок), поэтому перезапуск
 * трейдера посреди окна не даёт второй карточки по тому же окну.
 *
 * # Потолок
 *
 * Карточек единицы в час: по одной на живое окно серии, и то лишь когда замок
 * дошёл до торгового состояния. Потолок защищает не от потока, а от вкладки,
 * открытой сутками.
 */
const LOCK_LIMIT = 200;

interface LockSignalsState {
  /** Ключ — id карточки. Порядок показа задаёт сортировка, а не вставка. */
  signals: Record<string, LockSignal>;
  /** Одно сообщение сокета либо строка REST-списка. */
  ingest: (signal: LockSignal) => void;
  /** Пачка из REST: поднятие висящих карточек при подключении вкладки. */
  ingestMany: (signals: LockSignal[]) => void;
  /**
   * Сверка с ДОСТОВЕРНЫМ снимком сервера: чего в нём нет, того нет и на экране.
   *
   * `since` — момент ОТПРАВКИ запроса. Карточку, приехавшую сокетом позже него,
   * снимок знать не мог, и удалять её нельзя: гонка стоила бы оператору живого
   * предложения.
   */
  reconcile: (signals: LockSignal[], since: number) => void;
  clear: () => void;
}

/**
 * prune выбрасывает самые давние по времени обнаружения.
 *
 * По detected_at, а не по порядку прихода: REST отдаёт список сразу пачкой, и
 * порядок вставки в ней ничего не значит.
 */
function prune(next: Record<string, LockSignal>): Record<string, LockSignal> {
  const ids = Object.keys(next);
  if (ids.length <= LOCK_LIMIT) return next;
  const sorted = ids.sort(
    (a, b) => Date.parse(next[b]?.detected_at ?? "") - Date.parse(next[a]?.detected_at ?? ""),
  );
  for (const id of sorted.slice(LOCK_LIMIT)) delete next[id];
  return next;
}

export const useLockSignals = create<LockSignalsState>((setState) => ({
  signals: {},

  ingest: (signal) => {
    if (!signal?.id) return;
    setState((state) => ({ signals: prune({ ...state.signals, [signal.id]: signal }) }));
  },

  ingestMany: (incoming) => {
    if (incoming.length === 0) return;
    setState((state) => {
      const next = { ...state.signals };
      for (const s of incoming) {
        if (s?.id) next[s.id] = s;
      }
      return { signals: prune(next) };
    });
  },

  /**
   * # Почему сверка, а не только подстановка
   *
   * Карточка снимается с экрана сообщением сокета «отозвана» либо собственным
   * сроком. Оба способа отказывают там, где отказать хуже всего: Pub/Sub истории
   * не имеет, поэтому отзыв, случившийся при разорванной связи, до вкладки не
   * доедет НИКОГДА, а рестарт трейдера теряет карточку вместе с памятью процесса
   * — и на экране остаётся предложение сделки с живой кнопкой, которого сервер
   * уже не знает. Нажатие по нему уходит с идентификатором, которого нет.
   *
   * Пустой список — тоже ответ, и самый важный: «замков сейчас нет» приходит
   * ровно им. Поэтому сверка не выходит на пустом входе, в отличие от
   * ingestMany, где пустая пачка действительно ничего не меняет.
   *
   * # Что сверка НЕ трогает
   *
   * Карточки в терминальном состоянии (взята, пропущена, сгорела): сервер их не
   * отдаёт по построению — он отдаёт живые, — а на экран они и так не идут
   * (isLive требует `alerted`). Удаляй их сверка, и разница была бы только в
   * том, что журнал вкладки терял бы историю решений.
   */
  reconcile: (incoming, since) => {
    setState((state) => {
      const next = { ...state.signals };
      const known = new Set<string>();
      for (const s of incoming) {
        if (!s?.id) continue;
        next[s.id] = s;
        known.add(s.id);
      }
      for (const [id, s] of Object.entries(next)) {
        if (known.has(id)) continue;
        if (s.outcome !== "alerted") continue;
        // Приехала после снимка — снимок её знать не мог.
        if (Date.parse(s.detected_at) >= since) continue;
        delete next[id];
      }
      return { signals: prune(next) };
    });
  },

  clear: () => setState({ signals: {} }),
}));
